import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ButtonLink, Reveal, SectionLabel } from "@/components/common";
import { useFetch } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

const Group = ({ g, inverse }) => {
  const { categories } = useSite();
  const related = categories.filter((c) => g.category_slugs.includes(c.slug));
  return (
    <section id={g.key} data-testid={`service-group-${g.key}`} className={cn("scroll-mt-24 py-20 md:py-32", inverse && "inverse")}>
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="font-display text-[7rem] leading-none text-gold md:text-[10rem]">{g.number}</span>
          <h2 className="mt-2 font-display text-5xl uppercase leading-none md:text-7xl">{g.name}</h2>
          <p className="mt-6 font-display text-2xl italic text-turq">{g.tagline}</p>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <p className="text-base leading-relaxed text-silver md:text-lg">{g.description}</p>
          <ul className="mt-10 grid border-t border-line/15 sm:grid-cols-2 sm:gap-x-10">
            {g.items.map((it, i) => (
              <Reveal key={it} delay={i * 0.03}>
                <li className="flex items-baseline gap-4 border-b border-line/15 py-4 text-base"><span className="font-mono text-[10px] text-gold">{String(i + 1).padStart(2, "0")}</span>{it}</li>
              </Reveal>
            ))}
          </ul>
          {related.length > 0 && (
            <div className="mt-10">
              <p className="label-mono mb-4">Related work</p>
              <div className="flex flex-wrap gap-2">
                {related.map((c) => (
                  <Link key={c.slug} to={`/work?category=${c.slug}`} data-testid={`service-${g.key}-related-${c.slug}`} className="group inline-flex items-center gap-2 rounded-full border border-line/20 px-4 py-2 text-xs transition-colors hover:border-turq hover:text-turq">
                    <span className="font-mono text-gold">{c.number}</span>{c.name}<ArrowUpRight className="h-3 w-3 transition-transform group-hover:rotate-45" aria-hidden />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default function Services() {
  const { data } = useFetch("/services");
  const { settings } = useSite();
  useSeo({ title: settings && `Services — ${settings.name}`, description: "Creative, marketing, digital, technology and strategy — from the first idea to the working thing." });
  const { hash } = useLocation();
  useEffect(() => {
    if (hash && data) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [hash, data]);
  return (
    <div data-testid="services-page" className="pt-[72px]">
      <section className="container-x pb-16 pt-16 md:pb-24 md:pt-24">
        <SectionLabel number="—">Services</SectionLabel>
        <h1 className="mt-6 max-w-5xl font-display text-5xl leading-[0.95] sm:text-6xl lg:text-8xl">From the first idea to the <em className="text-turq">working</em> thing.</h1>
        <nav className="mt-14 flex flex-wrap gap-3" aria-label="Service groups">
          {(data || []).map((g) => (
            <a key={g.key} href={`#${g.key}`} data-testid={`services-jump-${g.key}`} className="rounded-full border border-line/20 px-5 py-2.5 text-sm transition-colors hover:border-turq hover:text-turq">
              <span className="mr-2 font-mono text-xs text-gold">{g.number}</span>{g.name}
            </a>
          ))}
        </nav>
      </section>
      {(data || []).map((g, i) => <Group key={g.id} g={g} inverse={i % 2 === 0} />)}
      <section className="container-x flex flex-col items-start gap-8 py-24 md:py-32">
        <p className="max-w-3xl font-display text-4xl leading-tight md:text-6xl">Not sure which box your project fits in? That's usually a good sign.</p>
        <div className="flex flex-wrap gap-3">
          <ButtonLink to="/contact?type=freelance" testId="services-start-project">Start a Project</ButtonLink>
          <ButtonLink to="/#what-can-we-build" variant="secondary" testId="services-what-can-we-build">What can we build?</ButtonLink>
        </div>
      </section>
    </div>
  );
}
